// Order Detection Analytics API Service

import api from './api';

export interface OrderDetectionOverview {
  total_detected: number;
  total_approved: number;
  total_rejected: number;
  total_cancelled: number;
  total_pending: number;
  approval_rate: number;
  rejection_rate: number;
  avg_confidence: number | null;
  avg_time_to_approval_hours: number | null;
}

export interface OrderDetectionByOutcome {
  approved_no_edits: number;
  approved_with_edits: number;
  rejected: number;
  cancelled: number;
  still_active: number;
  total: number;
}

export interface ConfidenceDistribution {
  bucket: string;
  count: number;
  approved_count: number;
  rejected_count: number;
}

export interface OrderDetectionTrend {
  period: string;
  detected: number;
  approved: number;
  rejected: number;
  approval_rate: number;
}

export interface SalespersonOrderStats {
  salesperson_name: string;
  salesperson_no: string;
  total_orders: number;
  approved: number;
  rejected: number;
  approval_rate: number;
  avg_time_to_action_hours: number | null;
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

export const orderAnalyticsService = {
  getOverview: async (dateFrom?: Date, dateTo?: Date): Promise<OrderDetectionOverview> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = formatDate(dateFrom);
    if (dateTo) params.date_to = formatDate(dateTo);
    const response = await api.get<OrderDetectionOverview>('/stats/orders/overview', { params });
    return response.data;
  },

  getByOutcome: async (dateFrom?: Date, dateTo?: Date): Promise<OrderDetectionByOutcome> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = formatDate(dateFrom);
    if (dateTo) params.date_to = formatDate(dateTo);
    const response = await api.get<OrderDetectionByOutcome>('/stats/orders/by-outcome', { params });
    return response.data;
  },

  getConfidenceDistribution: async (dateFrom?: Date, dateTo?: Date): Promise<ConfidenceDistribution[]> => {
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = formatDate(dateFrom);
    if (dateTo) params.date_to = formatDate(dateTo);
    const response = await api.get<ConfidenceDistribution[]>('/stats/orders/confidence', { params });
    return response.data;
  },

  getTrend: async (
    dateFrom?: Date,
    dateTo?: Date,
    granularity: string = 'month',
  ): Promise<OrderDetectionTrend[]> => {
    const params: Record<string, string> = { granularity };
    if (dateFrom) params.date_from = formatDate(dateFrom);
    if (dateTo) params.date_to = formatDate(dateTo);
    const response = await api.get<OrderDetectionTrend[]>('/stats/orders/trend', { params });
    return response.data;
  },

  getBySalesperson: async (
    dateFrom?: Date,
    dateTo?: Date,
    limit: number = 10,
  ): Promise<SalespersonOrderStats[]> => {
    const params: Record<string, string | number> = { limit };
    if (dateFrom) params.date_from = formatDate(dateFrom);
    if (dateTo) params.date_to = formatDate(dateTo);
    const response = await api.get<SalespersonOrderStats[]>('/stats/orders/by-salesperson', { params });
    return response.data;
  },
};
